import { Icons } from '@ohif/ui-next';
import React from 'react';

/**
 * Drop-in replacement for the old `@ohif/ui` `<Icon name="kebab-name" />`.
 *
 * OHIF 3.10 emptied the `@ohif/ui` icon registry (icons moved to the
 * `@ohif/ui-next` `Icons` set), so every `@ohif/ui` `<Icon name=... />` renders
 * "Missing Icon". This wrapper resolves the name against the ui-next `Icons`
 * registry, which keys some icons in kebab-case (`settings`, `chevron-down`) and
 * others in PascalCase (`ChevronLeft`, `GroupLayers`, `SidePanelCloseLeft`).
 */

// Names whose ui-next equivalent isn't a straight kebab→Pascal conversion.
const ALIASES: Record<string, string> = {
  'push-left': 'SidePanelCloseLeft',
  'push-right': 'SidePanelCloseRight',
  // Collapsed side-panel reveal arrow — ui-next renamed it (dropping the
  // `-right`), so kebab→Pascal (`NavigationPanelRightReveal`) misses.
  'navigation-panel-right-reveal': 'NavigationPanelReveal',
};

const toPascalCase = (name: string): string =>
  name.replace(/(^|-)([a-z0-9])/g, (_, __, c) => c.toUpperCase());

function resolveName(name: string): string {
  if (Icons[name]) {
    return name;
  }
  if (ALIASES[name] && Icons[ALIASES[name]]) {
    return ALIASES[name];
  }
  const pascal = toPascalCase(name);
  if (Icons[pascal]) {
    return pascal;
  }
  return name;
}

type IconProps = {
  name: string;
  className?: string;
  [key: string]: unknown;
};

export function Icon({ name, ...props }: IconProps) {
  return <Icons.ByName name={resolveName(name)} {...props} />;
}

export default Icon;
